import { IntlShape } from "react-intl";
import { LinePretensionType } from "src/helpers/enums";
import { LineProfile, VesselScenario } from "src/interfaces/VesselScenario";
import { VesselScenarioStore } from "src/stores/VesselScenarioStore";

type MessageItem = { title: string; description: string };

const getLineProfile = (lineProfileId: number, lineProfiles: LineProfile[]) => {
    return lineProfiles.find(
        (profile) => profile.lineProfileId === lineProfileId,
    );
};

/**
 * Get validation messages for VaryingPretensionType where each line has its own pretension.
 * Note: ValuePretensionType and RangePretensionType are validated via validateField in validators.ts
 */
function getLineVaryingPretensionExceedsLDBFMessages(vesselScenarioStore: VesselScenarioStore, intl: IntlShape): MessageItem[] {
    const { scenarioData, mooringArrangement } = vesselScenarioStore;
    const mooring = scenarioData?.data?.mooring;

    if (!mooring || !mooringArrangement?.length) return [];
    if (mooring.linePretensionType !== LinePretensionType.VaryingPretensionType) return [];

    const lineProfiles = scenarioData?.data?.vessel?.lineProfiles || [];

    return mooringArrangement
        .map<MessageItem | null>((ma) => {
            const mbl = getLineProfile(ma.lineProfileId, lineProfiles)?.minimumBreakingLoad;
            if (typeof mbl !== 'number' || ma.pretension < mbl) return null;

            return {
                title: `${ma.lineName} #${ma.id}`,
                description: intl.formatMessage(
                    {
                        id: 'components.ma.forms.pretensionExceedsLDBF',
                        defaultMessage: 'Pretension ({pretension}t) exceeds Line Design Break Force ({mbl}t).',
                    },
                    { pretension: ma.pretension, mbl }
                ),
            };
        })
        .filter(Boolean) as MessageItem[];
}

/**
 * Check if pretension value exceeds any line's Line Design Break Force (LDBF)
 */
const checkPretensionExceedsLDBF = (pretensionValue: number, scenarioData: VesselScenario): boolean => {
    if (!('vessel' in scenarioData) || !('mooring' in scenarioData)) return false;

    const lineProfiles = scenarioData?.vessel?.lineProfiles;

    if (!lineProfiles) return false;

    const { mooring } = scenarioData;
    const mooringArrangement = mooring.mooringArrangements?.[mooring.mooringArrangementName];

    return mooringArrangement?.some(({ lineProfileId }) => {
        const mbl = getLineProfile(lineProfileId, lineProfiles)?.minimumBreakingLoad;
        return typeof mbl === 'number' && pretensionValue >= mbl;
    }) ?? false;
};

export { checkPretensionExceedsLDBF, getLineProfile, getLineVaryingPretensionExceedsLDBFMessages, type MessageItem };
